// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import {
  withStyles,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@material-ui/core';
import { shape, bool, arrayOf, func, string } from 'prop-types';

import {
  AlertMessages,
  FormTextField,
  RadioGroupSection,
  FormActionButtons,
  Loading,
  ErrorContainer,
  HelpContainer,
  FormSelect,
} from '../../../components';
import { fetch, save, update } from '../../../actions/rateQuota';
import {
  getUserDetails,
  getFeatureFlagForDistributeRateLimit,
} from '../../../reducers/portalConfig';
import {
  getIsLoading,
  getIsError,
  getErrors,
  getIsSaveSuccess,
  getDetails,
} from '../../../reducers/rateQuota';
import { hasError, isNumberOnly } from '../../../utils';
import { getI18n } from '../../../utils/intl';
import {
  ALERT_ERROR,
  QUOTAS_INTERVAL_API,
  QUOTAS_INTERVAL_ORG,
  QUOTA_ASSIGNMENT_ORG,
  QUOTA_ASSIGNMENT_PRODUCT_PER_APPLICATION,
  QUOTAS_INTERVAL_PRODUCT_PER_APPLICATION,
  RATE_QUOTA_ASSIGNMENT_LEVEL_WITH_PRODUCT,
  RATE_QUOTA_HELP_ITEMS_WITH_PRODUCT,
  DISTRIBUTED_RATE_LIMIT_INTERVAL_API,
} from '../../../constants';
import { BreadcrumbsForPages } from '../../../pageComponents';
import { RATE_QUOTAS_DETAILS } from '../../../pageComponents/BreadcrumbsForPages/constants';
import styles from './styles';

const getUuid = (props) =>
  get(props, 'match.path') &&
  get(props, 'match.path').includes('/edit/') &&
  get(props, 'match.params.rqUuid');

export const RateQuotaEdit = (props) => {
  const {
    classes,
    userContext,
    featureFlagDistributeRateLimit,
    isLoading,
    rateQuotaErrors,
    isError,
    isSaveSuccess,
    details,
    pathName,
  } = props;

  const uuid = getUuid(props) || '';

  const getHeaderTitle = () => {
    if (uuid && details) {
      return `edit ${
        details.name
      }`;
    }
    return intl.getI18nMessage('label.breadcrumb.add');
  };

  const minNumberValue = 1;
  const maxNumberValue = 2000000000;
  const intl = getI18n(useIntl());
  const [name, setName] = useState('');
  const [isNameError, setIsNameError] = useState(false);
  const [assignmentLevel, setAssigmentLevel] = useState('API');
  const [isRate, setIsRate] = useState(false);
  const [rateLimit, setRateLimit] = useState('');
  const [spreadWindow, setSpreadWindow] = useState('');
  const [maxConcurrency, setMaxConcurrency] = useState('');
  const [rateLimitError, setRateLimitError] = useState(false);
  const [spreadWindowError, setSpreadWindowError] = useState(false);
  const [maxConcurrencyError, setMaxConcurrencyError] = useState(false);
  const [isQuota, setIsQuota] = useState(false);
  const [quotaInterval, setQuotaInterval] = useState();
  const [listIntervals, setListIntervals] = useState(QUOTAS_INTERVAL_API);
  const [listDistributedRateLimitIntervals,
    setListDistributedRateLimitIntervals] = useState(DISTRIBUTED_RATE_LIMIT_INTERVAL_API);
  const [quota, setQuota] = useState('');
  const [quotaError, setQuotaError] = useState(false);
  const [description, setDescription] = useState();
  const [isDistributedRate, setIsDistributedRate] = useState(false);
  const [distributedRateLimit, setDistributedRateLimit] = useState('');
  const [distributedInterval, setDistributedInterval] = useState('');
  const [distributedBurstLimit, setDistributedBurstLimit] = useState('');
  const [distributedRateLimitError, setDistributedRateLimitError] = useState('');
  const [distributedBurstLimitError, setDistributedBurstLimitError] = useState('');
  const [errors, setErrors] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [headerTitle, setHeaderTitle] = useState(getHeaderTitle());
  const [breadcrubItems, setBreadcrumbItems] = useState([])

  const notifyMessages = (message, status) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const clearErrorMessages = () => {
    notifyMessages('', '');
    setErrors([]);
  };

  useEffect(() => {
    if (
      !uuid &&
      !get(userContext, 'permissions.RATE_QUOTA', []).includes('CREATE')
    ) {
      props.push('/404');
    } else if (
      uuid &&
      !get(userContext, 'permissions.RATE_QUOTA', []).includes('UPDATE')
    ) {
      props.push('/404');
    }
  }, [userContext]);

  useEffect(() => {
    if (uuid) {
      props.fetch(uuid);
    }
  }, [uuid]);

  
  useEffect(() => {
    setBreadcrumbItems([
      ...RATE_QUOTAS_DETAILS, ...[
      { isActive: false, link: '', linkName: headerTitle },
    ]]);
  }, [pathName, headerTitle]);

  useEffect(() => {
    if (details && details.uuid) {
      setName(details.name);
      setAssigmentLevel(details.assignmentLevel);
      setDescription(details.description);
      if (get(details, 'rateLimit.maxRequestsPerSecond')) {
        setIsRate(true);
        setRateLimit(get(details, 'rateLimit.maxRequestsPerSecond').toString());
        if (get(details, 'rateLimit.windowSizeInSeconds'))
          setSpreadWindow(
            get(details, 'rateLimit.windowSizeInSeconds').toString(),
          );
        if (get(details, 'rateLimit.maxConcurrency'))
          setMaxConcurrency(
            get(details, 'rateLimit.maxConcurrency').toString(),
          );
      }
      if (get(details, 'distributedRateLimit.maxRequests')) {
        setIsDistributedRate(true);
        setDistributedRateLimit(get(details, 'distributedRateLimit.maxRequests').toString());
        setDistributedInterval(get(details, 'distributedRateLimit.interval'));
        if (get(details, 'distributedRateLimit.burstLimit'))
          setDistributedBurstLimit(
            get(details, 'distributedRateLimit.burstLimit').toString(),
          );
      }
      if (get(details, 'quota.quota')) {
        setIsQuota(true);
        setQuotaInterval(get(details, 'quota.interval'));
        setQuota(get(details, 'quota.quota').toString());
      }
      if (details.assignmentLevel === QUOTA_ASSIGNMENT_ORG) {
        setListIntervals(QUOTAS_INTERVAL_ORG);
      }
      if (
        details.assignmentLevel === QUOTA_ASSIGNMENT_PRODUCT_PER_APPLICATION
      ) {
        setListIntervals(QUOTAS_INTERVAL_PRODUCT_PER_APPLICATION);
      }
      setListDistributedRateLimitIntervals(DISTRIBUTED_RATE_LIMIT_INTERVAL_API);
      setHeaderTitle(getHeaderTitle());
    }
  }, [details]);

  useEffect(() => {
    if (isError) {
      notifyMessages(intl.getI18nMessage('error.title'), ALERT_ERROR);
      setErrors(rateQuotaErrors);
    } else if (!isNameError) {
      clearErrorMessages();
    }
  }, [isError, rateQuotaErrors]);

  useEffect(() => {
    if (isSaveSuccess) {
      clearErrorMessages();
      const id = details && details.uuid ? details.uuid : uuid;
      props.push(`/admin/rate-quotas/details/${id}`);
    }
  }, [isSaveSuccess, details]);

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'name': {
        setName(value);
        setIsNameError(hasError(value, true));
        break;
      }
      case 'assignmentLevel': {
        setAssigmentLevel(value);
        if (value === QUOTA_ASSIGNMENT_ORG) {
          setListIntervals(QUOTAS_INTERVAL_ORG);
        } else if (value === QUOTA_ASSIGNMENT_PRODUCT_PER_APPLICATION) {
          setListIntervals(QUOTAS_INTERVAL_PRODUCT_PER_APPLICATION);
        } else {
          setListIntervals(QUOTAS_INTERVAL_API);
        }
        break;
      }
      case 'ratelimit': {
        const isRateError =
            validateNumberField(value, true, minNumberValue, maxNumberValue);
        setRateLimit(value);
        setRateLimitError(isRateError);
        break;
      }
      case 'spreadWindow': {
        const isSpreadError = value
          ? validateNumberField(value)
          : false;
        setSpreadWindow(value);
        setSpreadWindowError(isSpreadError);
        break;
      }
      case 'maxConcurrency': {
        const isMaxConcurrencyError = value
          ? validateNumberField(value)
          : false;
        setMaxConcurrency(value);
        setMaxConcurrencyError(isMaxConcurrencyError);
        break;
      }
      case 'quota': {
        const isQuotaError =
            validateNumberField(value, true, minNumberValue, maxNumberValue);
        setQuotaError(isQuotaError);
        setQuota(value);
        break;
      }
      case 'distributedBurstLimit': {
        const isDistributedBurstLimitError =
            validateNumberField(value, false, Number(distributedRateLimit), maxNumberValue);
        setDistributedBurstLimit(value);
        setDistributedBurstLimitError(isDistributedBurstLimitError);
        break;
      }
      case 'distributedRateLimit': {
        const isDistributedRateError =
            validateNumberField(value, true, minNumberValue, maxNumberValue);
        setDistributedRateLimit(value);
        setDistributedRateLimitError(isDistributedRateError);
        break;
      }
      case 'description':
        setDescription(value);
        break;
      default:
        break;
    }
  };

  const validateNumberField = (value, isRequired = false, min = 0, max = Number.MAX_VALUE) => {
    return (
          hasError(value, isRequired) ||
          isNaN(parseInt(value)) ||
          isNumberOnly(value) ||
          value < min ||
          value > max
    );
  }

  const handleQuotaIntervalChange = (value) => {
    setQuotaInterval(value);
  };

  const handleDistributedIntervalChange = (value) => {
    setDistributedInterval(value);
  }

  const handleRateChecked = (e) => {
    setIsRate(e.target.checked);
    if (!e.target.checked) {
      setRateLimit('');
      setSpreadWindow('');
      setMaxConcurrency('');
    } else {
      if (uuid) {
        if (get(details, 'rateLimit.maxRequestsPerSecond'))
          setRateLimit(
            get(details, 'rateLimit.maxRequestsPerSecond').toString(),
          );
        if (get(details, 'rateLimit.windowSizeInSeconds'))
          setSpreadWindow(
            get(details, 'rateLimit.windowSizeInSeconds').toString(),
          );
        if (get(details, 'rateLimit.maxConcurrency'))
          setMaxConcurrency(
            get(details, 'rateLimit.maxConcurrency').toString(),
          );
      }
    }
  };

  const handleDistributedRateChecked = (e) => {
    setIsDistributedRate(e.target.checked);
    if (!e.target.checked) {
      setDistributedRateLimit('');
      setDistributedInterval(null);
      setDistributedBurstLimit('');
    } else {
      if (uuid) {
        if (get(details, 'distributedRateLimit.maxRequests'))
          setDistributedRateLimit(
            get(details, 'distributedRateLimit.maxRequests').toString(),
          );

        setDistributedInterval(
          get(details, 'distributedRateLimit.interval')
              ? get(details, 'distributedRateLimit.interval').toString()
              : listDistributedRateLimitIntervals[0].uuid,
        );

        if (get(details, 'distributedRateLimit.burstLimit'))
          setDistributedBurstLimit(
            get(details, 'distributedRateLimit.burstLimit').toString(),
          );
      } else {
          setDistributedInterval(listDistributedRateLimitIntervals[0].uuid);
      }
    }
  };

  const handleQuotaChecked = (e) => {
    setIsQuota(e.target.checked);
    if (!e.target.checked) {
      setQuotaInterval(null);
      setQuota('');
    } else {
      if (uuid) {
        setQuotaInterval(
          get(details, 'quota.interval')
            ? get(details, 'quota.interval').toString()
            : listIntervals[0].uuid,
        );
        setQuota(
          get(details, 'quota.quota') ? get(details, 'quota.quota').toString() : '',
        );
      } else {
        setQuotaInterval(listIntervals[0].uuid);
      }
    }
  };

  const onCancel = () => {
    clearErrorMessages();
    window.location.href = '/admin/rate-quotas/';
  };
  const hasValidSaveButton = () => {
    const nameUiError = hasError(name, true);
    const quotaUiError =
      isQuota && validateNumberField(quota, true, minNumberValue, maxNumberValue);
    const rateUiError =
      isRate && validateNumberField(rateLimit, true, minNumberValue, maxNumberValue);
    const spreadWindowUiError = spreadWindow
      ? validateNumberField(spreadWindow)
      : false;
    const maxCurrencyUiError = maxConcurrency
      ? validateNumberField(maxConcurrency)
      : false;

    const distributedRateLimitUiError =
        isDistributedRate &&
        validateNumberField(distributedRateLimit, true, minNumberValue, maxNumberValue);
    const distributedBurstLimitUiError =
        isDistributedRate &&
        validateNumberField(
            distributedBurstLimit, false, Number(distributedRateLimit), maxNumberValue);

    setIsNameError(nameUiError);
    setQuotaError(quotaUiError);
    setRateLimitError(rateUiError);
    setSpreadWindowError(spreadWindowUiError);
    setMaxConcurrencyError(maxCurrencyUiError);
    setDistributedRateLimitError(distributedRateLimitUiError);
    setDistributedBurstLimitError(distributedBurstLimitUiError);

    const hasUiError = (
      nameUiError ||
      quotaUiError ||
      rateUiError ||
      spreadWindowUiError ||
      maxCurrencyUiError ||
      distributedRateLimitUiError ||
      distributedBurstLimitUiError
    );

    if ( hasUiError ) {
      notifyMessages(
        intl.getI18nMessage('error.required.fields.valid'),
        ALERT_ERROR,
      );
    }
    return hasUiError;
  };

  const onNext = async () => {
    clearErrorMessages();
    if (!hasValidSaveButton()) {
      const data = {
        name,
        assignmentLevel,
        description,
        quota: {
          quota,
          interval: quotaInterval,
        },
        rateLimit: {
          maxRequestsPerSecond: rateLimit,
          windowSizeInSeconds: spreadWindow,
          maxConcurrency: maxConcurrency,
        },
      };

      //Adding distributed Rate limit object in payload only if feature flag is enabled
      if (featureFlagDistributeRateLimit) {
        data.distributedRateLimit = {
          maxRequests: distributedRateLimit,
          interval: distributedInterval,
          burstLimit: distributedBurstLimit,
        };
      }

      if (uuid) {
        data.uuid = uuid;
        await props.update({ uuid, data });
      } else {
        await props.save({ data });
      }
    }
  };

  return (
    <div
      className={classes.content}
      id="rate-quota-edit-page"
      data-apim-test="rate-quota-edit-page"
    >
      {isLoading && <Loading pageLoader />}
      <Grid container className={classes.gridContainer}>
        <Grid
          md={2}
          item
          className={classes.leftSideBar}
          id="rate-quota-edit-left-sidebar"
          data-apim-test="rate-quota-edit-left-sidebar"
        />
        <Grid
          md={7}
          item
          className={classes.mainContent}
          id="rate-quota-edit-main-content"
          data-apim-test="rate-quota-edit-main-content"
        >
          <BreadcrumbsForPages pageContainerClass={classes.pageBreadCrumbClass}
            pathName={pathName} items={breadcrubItems} />
          {notificationMessage && (
            <AlertMessages
              id="rate-quota-edit-notification"
              data-apim-test="rate-quota-edit-notification"
              message={notificationMessage}
              variant={notificationStatus}
              onClose={() => {
                notifyMessages('', '');
              }}
            />
          )}
          {errors.length > 0 && <ErrorContainer errors={errors} />}
          <FormTextField
            id={'rate-quota-edit-page-name'}
            name={intl.getI18nMessage('label.rate.quota.name')}
            value={name}
            error={isNameError}
            maxLength={'50'}
            handleChange={(value) => handleChange('name', value)}
            helperText={intl.getI18nMessage('label.rate.quota.name.help')}
          />
          <h4 className={classes.formLabel}>
            {intl.getI18nMessage('label.rate.quota.assignment.level')}
          </h4>
          <RadioGroupSection
            selected={assignmentLevel}
            onChange={(e) => handleChange('assignmentLevel', e.target.value)}
            data={RATE_QUOTA_ASSIGNMENT_LEVEL_WITH_PRODUCT}
            id={'rate-quota-edit-page-assignment-level'}
            defaultSelect={''}
            isDisable={uuid ? true : false}
          />
          <h4 className={classes.formLabel}>
            {intl.getI18nMessage('label.rate.quota.limit')}
          </h4>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  id={'rate-quota-edit-page-isRate'}
                  classes={{ root: classes.iconRoot }}
                  checked={isRate}
                  onChange={handleRateChecked}
                />
              }
              classes={{ root: classes.limitLabel }}
              label={intl.getI18nMessage('label.rate.quota.limit.rate')}
            />
            {isRate && (
              <div className={classes.inputContainer}>
                <FormTextField
                  id={'rate-quota-edit-page-rate-limit'}
                  fieldContainerClass={classes.fieldTextContainer}
                  name={intl.getI18nMessage('label.rate.quota.rate.limit')}
                  value={rateLimit}
                  error={rateLimitError}
                  maxLength={'10'}
                  handleChange={(value) => handleChange('ratelimit', value)}
                  helperText={intl.getI18nMessage(
                    'label.rate.quota.limit.help',
                  )}
                />
                <FormTextField
                  id={'rate-quota-edit-page-spread-limit-window'}
                  fieldContainerClass={classes.fieldTextContainer}
                  name={intl.getI18nMessage(
                    'label.rate.quota.spread.limit.window',
                  )}
                  error={spreadWindowError}
                  value={spreadWindow}
                  maxLength={'10'}
                  handleChange={(value) =>
                    handleChange('spreadWindow', value)
                  }
                  helperText={intl.getI18nMessage('label.seconds')}
                />
                <FormTextField
                  id={'rate-quota-edit-page-maximum-concurrency'}
                  fieldContainerClass={classes.fieldTextContainer}
                  name={intl.getI18nMessage(
                    'label.rate.quota.maximum.concurrency',
                  )}
                  error={maxConcurrencyError}
                  value={maxConcurrency}
                  maxLength={'10'}
                  handleChange={(value) =>
                    handleChange('maxConcurrency', value)
                  }
                  helperText={intl.getI18nMessage('label.requests')}
                />
              </div>
            )}
            {featureFlagDistributeRateLimit && (
              <FormControlLabel
                control={
                  <Checkbox
                    id={'rate-quota-edit-page-isDistributedRate'}
                    classes={{ root: classes.iconRoot }}
                    checked={isDistributedRate}
                    onChange={handleDistributedRateChecked}
                  />
                }
                classes={{ root: classes.limitLabel }}
                label={intl.getI18nMessage('label.rate.quota.limit.distributed.rate')}
              />
             )}
            {featureFlagDistributeRateLimit && isDistributedRate && (
              <div className={classes.inputContainer}>
                <FormTextField
                  id={'rate-quota-edit-page-distributed-rate-limit'}
                  fieldContainerClass={classes.fieldTextContainer}
                  name={intl.getI18nMessage('label.rate.quota.distributed.rate.limit')}
                  value={distributedRateLimit}
                  error={distributedRateLimitError}
                  maxLength={'10'}
                  handleChange={(value) => handleChange('distributedRateLimit', value)}
                  helperText={intl.getI18nMessage(
                    'label.rate.quota.limit.help',
                  )}
                />
                <FormSelect
                  {...props}
                  id={'rate-quota-edit-page-distributed-rate-interval'}
                  name={intl.getI18nMessage(
                    'label.rate.quota.limit.distributed.rate.limit.interval',
                  )}
                  fieldContainerClass={classes.fieldTextContainer}
                  value={distributedInterval}
                  data={listDistributedRateLimitIntervals}
                  handleChange={handleDistributedIntervalChange}
                  noNoneOption
                  noNativeSelect
                />
                <FormTextField
                  id={'rate-quota-edit-page-distributed-burst'}
                  name={intl.getI18nMessage(
                    'label.rate.quota.limit.distributed.burst',
                  )}
                  error={distributedBurstLimitError}
                  value={distributedBurstLimit}
                  maxLength={'10'}
                  handleChange={(value) =>
                    handleChange('distributedBurstLimit', value)
                  }
                  helperText={intl.getI18nMessage('label.rate.quota.limit.distributed.burst.limit.help')}
                />
              </div>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  id={'rate-quota-edit-page-isQuota'}
                  classes={{ root: classes.iconRoot }}
                  checked={isQuota}
                  onChange={handleQuotaChecked}
                />
              }
              classes={{ root: classes.limitLabel }}
              label={intl.getI18nMessage('label.rate.quota.limit.quota')}
            />
            {isQuota && (
              <div className={classes.inputContainer}>
                <FormSelect
                  {...props}
                  id={'rate-quota-edit-page-quota-interval'}
                  name={intl.getI18nMessage(
                    'label.rate.quota.limit.quota.interval',
                  )}
                  fieldContainerClass={classes.fieldTextContainer}
                  value={quotaInterval}
                  data={listIntervals}
                  handleChange={handleQuotaIntervalChange}
                  noNoneOption
                  noNativeSelect
                />
                <FormTextField
                  id={'rate-quota-edit-page-quota'}
                  name={intl.getI18nMessage('label.rate.quota.limit.quota')}
                  value={quota}
                  error={quotaError}
                  maxLength={'10'}
                  handleChange={(value) => handleChange('quota', value)}
                  helperText={intl.getI18nMessage(
                    'label.rate.quota.limit.help',
                  )}
                />
              </div>
            )}
          </FormGroup>
          <FormTextField
            id={'rate-quota-edit-page-description'}
            name={intl.getI18nMessage('label.rate.quota.description')}
            value={description}
            handleChange={(value) => handleChange('description', value)}
            helperText={intl.getI18nMessage(
              'label.rate.quota.description.help',
            )}
            multiline
            rows={4}
            optional
            maxLength={'255'}
          />
        </Grid>
        <Grid
          md={3}
          item
          className={classes.rightSideBar}
          id="rate-quota-edit-right-sidebar"
          data-apim-test="rate-quota-edit-right-sidebar"
        >
          <HelpContainer helpItems={RATE_QUOTA_HELP_ITEMS_WITH_PRODUCT} />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        onCancelClick={onCancel}
        nextText={intl.getI18nMessage('label.save.button')}
        cancelText={intl.getI18nMessage('label.cancel.button')}
        id="rate-quota-edit-form-buttons"
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isError: getIsError(state),
  isLoading: getIsLoading(state),
  rateQuotaErrors: getErrors(state),
  isSaveSuccess: getIsSaveSuccess(state),
  details: getDetails(state),
  userContext: getUserDetails(state),
  featureFlagDistributeRateLimit: getFeatureFlagForDistributeRateLimit(state),
  pathName: get(state, 'router.location.pathname'),
});

const mapDispatchToProps = {
  fetch,
  save,
  update,
  push,
};

RateQuotaEdit.propTypes = {
  classes: shape({}),
  assets: shape({}),
  isLoading: bool,
  isError: bool,
  isSaveSuccess: bool,
  rateQuotaErrors: arrayOf(shape({})),
  fetch: func,
  save: func,
  update: func,
  push: func,
  userContext: shape({}),
  featureFlagDistributeRateLimit: bool,
  details: shape({}),
  pathName: string,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(RateQuotaEdit);
